import { styled } from '@porsche-code/kado-styled-system/jsx';
import { ActionGroup, Spacer } from '@porsche-code/kado-ui';
import {
  PButton,
  PHeading,
  PInlineNotification,
  PText,
  PTextarea,
  useToastManager,
} from '@porsche-design-system/components-react';
import { ClientError } from 'graphql-request';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { useCreateUserFeedbackMutation } from '@/graphql';

import { StarsOutput } from '../output';

type UserFeedbackFormSubmitProps = {
  rating: number;
  comment: string;
};

type UserFeedbackFormProps = {
  onDismiss: () => void;
};

const StarRatingContainer = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
  },
});

const CenterItems = styled('div', {
  base: {
    justifyItems: 'center',
  },
});

export const UserFeedbackForm = ({ onDismiss }: UserFeedbackFormProps) => {
  const { t } = useTranslation(NAMESPACES);
  const { addMessage } = useToastManager();
  const [hover, setHover] = useState(0);

  const formMethods = useForm<UserFeedbackFormSubmitProps>({
    defaultValues: {
      rating: 0,
      comment: '',
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = formMethods;

  const { mutateAsync: createUserFeedback, error } =
    useCreateUserFeedbackMutation({
      onSuccess: () => {
        addMessage({
          text: t('userFeedback.form.thankYouMessage'),
          state: 'success',
        });
        onDismiss();
      },
    });

  const onSubmit = async (data: UserFeedbackFormSubmitProps) => {
    try {
      await createUserFeedback({
        input: data,
      });
    } catch {
      // Error is shown in the form
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PHeading slot="header" size="lg" tag="h2">
          {t('userFeedback.form.title')}
        </PHeading>
        {!!error && (
          <>
            <Spacer mb="md" />
            <PInlineNotification
              heading={t('common:error')}
              description={`${
                error instanceof ClientError
                  ? (error.response.errors?.[0].message ?? error.message)
                  : `${error}`
              }`}
              state="error"
            />
          </>
        )}
        <Spacer mb="md" />
        <PText>{t('userFeedback.form.ratingDescription')}</PText>
        <Spacer mb="lg" />
        <Controller
          name="rating"
          control={control}
          rules={{
            min: { value: 1, message: t('userFeedback.form.ratingRequired') },
          }}
          render={({ field }) => (
            <StarRatingContainer>
              <StarsOutput
                rating={field.value}
                hover={hover}
                onStarClick={(starValue) =>
                  setValue('rating', starValue, { shouldValidate: true })
                }
                onStarMouseEnter={(starValue) => setHover(starValue)}
                onStarMouseLeave={() => setHover(0)}
                size="xl"
                role="button"
                aria-label="star"
              />
            </StarRatingContainer>
          )}
        />
        {errors.rating && (
          <CenterItems>
            <PText color="error">{errors.rating.message}</PText>
          </CenterItems>
        )}
        {errors.rating ? <Spacer mb="md" /> : <Spacer mb="lg" />}
        <PText>{t('userFeedback.form.commentDescription')}</PText>
        <Spacer mb="md" />
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <PTextarea
              {...field}
              name="comment"
              aria-label="comment"
              placeholder={t('userFeedback.form.placeholder')}
              maxLength={1000}
              counter
            />
          )}
        />
        <Spacer mb="md" />

        <PText size="xs">{t('userFeedback.form.advice')}</PText>

        <ActionGroup>
          <PButton
            name="cancel"
            aria-label="cancel"
            variant="secondary"
            type="button"
            onClick={onDismiss}
            disabled={isSubmitting}
          >
            {t('common:action.cancel')}
          </PButton>
          <PButton
            name="save"
            aria-label="save"
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t('common:action.save')}
          </PButton>
        </ActionGroup>
      </form>
    </FormProvider>
  );
};
